import { Injectable, inject } from "@angular/core";
import { AbstractTransformer } from "../abstract-transformer";
import { Transform, TRANSFORM_TYPE } from "@pienso/ingest-types/transform.interface";
import { CorpusDataService, TokenizeSentencesDetails, TransformDetails } from "../../services/corpus-data.service";
import { AlertService } from "@ui";
import { SentenceChunkService } from "./sentence-chunk.service";
import { TransformerGridDataService } from "../transformer-grid-data.service";
import { ToolbarItem } from "../toolbar-item";
import { SentenceChunkToolbarComponent } from "./sentence-chunk-toolbar.component";
import { TRANSFORMER_STATE } from "../abstract-transformer.service";
import { Corpus } from "@pienso/ingest-types/corpus.interface";
import { skipWhile, take } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class SentenceChunkTransformerService extends AbstractTransformer {
  override service: SentenceChunkService;
  protected override dataService: TransformerGridDataService;

  constructor() {
    const service = inject(SentenceChunkService);
    const dataService = inject(TransformerGridDataService);
    const corpusService = inject(CorpusDataService);
    const alertService = inject(AlertService);

    super(
      "chunk",
      "Split",
      [TRANSFORM_TYPE.TOKENIZE_SENTENCES],
      corpusService,
      alertService,
      service,
      dataService,
      new ToolbarItem(SentenceChunkToolbarComponent),
    );
    this.service = service;
    this.dataService = dataService;

    service.sentencesPerChunk$.subscribe((size) => {
      if (service.state !== TRANSFORMER_STATE.EDITING) {
        service.state = TRANSFORMER_STATE.EDITING;
      }
    });
  }

  public describe(xform: Transform) {
    const d: any = xform.details;
    switch (xform.transformType as TRANSFORM_TYPE) {
      case TRANSFORM_TYPE.TOKENIZE_SENTENCES:
        return `Split documents into groups of sentences`;
      default:
        throw new Error("Incompatible transform type for Column Types transform: " + xform.transformType);
    }
  }

  override init(corpus: Corpus) {
    this.reset();
    const promise = super.init(corpus);
    this.dataService.gridData$
      .pipe(
        skipWhile((data) => !data),
        take(1),
      )
      .subscribe((data) => (this.service.columnTypes = data?.columnInfo.columnTypes ?? null));
    return promise;
  }

  protected createSavePayload(): TransformDetails {
    if (!this.service.columnId) {
      throw new Error("Column ID is not set");
    }
    return new TokenizeSentencesDetails({
      field: this.service.columnId,
      sentencesPerChunk: this.service.sentencesPerChunk,
    });
  }

  protected initFromTransform(xform: Transform) {
    if (xform.transformType !== TRANSFORM_TYPE.TOKENIZE_SENTENCES) {
      throw new Error("Incompatible transform details for sentence chunk transformer: " + xform.transformType);
    }
    const d = <any>xform.details;
    this.service.columnId = d.field;
    this.service.sentencesPerChunk = d.sentencesPerChunk;
  }
}
